import { useState } from 'react'

import { api } from '../api/client'
import { CategoryBars, CountSeries, Donut, StatusStrip } from '../components/charts'
import type { DonutSlice, StatusSlice } from '../components/charts'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import type { WazuhCveHost, WazuhOverview } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { Connessioni } from '../components/Connessioni'
import { t } from '../i18n/lingua'

/** Colore per gravità di una vulnerabilità: rosso→ambra→blu→grigio, mai verde
 *  (una vulnerabilità «bassa» resta una vulnerabilità, non un via libera). */
const COLORE_GRAVITA: Record<string, string> = {
  critical: 'var(--error)',
  high: 'var(--warn)',
  medium: 'var(--info)',
  low: 'var(--text-muted)',
}

function coloreGravita(severity: string): string {
  return COLORE_GRAVITA[severity.trim().toLowerCase()] ?? 'var(--muted-bg)'
}

/** Le finestre temporali offerte per gli allarmi. Un elenco chiuso: il servizio
 *  accetta solo questi valori. */
const FINESTRE = [7, 14, 30] as const

type RegolaWazuh = NonNullable<WazuhOverview['alerts']>['by_rule'][number]
type CveWazuh = NonNullable<WazuhOverview['vulnerabilities']>['top_cves'][number]

/**
 * La dashboard Wazuh del parco: agenti, allarmi (per livello, per regola,
 * andamento) e vulnerabilità (per gravità, CVE più diffuse, host più colpiti).
 * Complementare alla sezione «Wazuh» del singolo CI. Dati in tempo reale,
 * chiesti a Wazuh attraverso la sonda — non salvati.
 */
export default function Wazuh() {
  const [giorni, setGiorni] = useState<number>(7)
  const dati = useAsync(() => api.wazuhOverview(giorni), [giorni])
  const d = dati.data
  // The CVE rows opened on the hosts they hit.
  const [cveAperte, setCveAperte] = useState<Set<string>>(new Set())

  function alternaCve(cve: string) {
    setCveAperte((prima) => {
      const dopo = new Set(prima)
      if (dopo.has(cve)) dopo.delete(cve)
      else dopo.add(cve)
      return dopo
    })
  }

  const colonneRegole: Column<RegolaWazuh>[] = [
    { key: 'level', header: t('Livello'), type: 'number', align: 'left', render: (r) => (
      <span className={`badge${(r.level ?? 0) >= 10 ? ' badge--error' : ' badge--warn'}`}>
        {r.level ?? '—'}
      </span>
    ) },
    { key: 'rule', header: t('Regola'), value: (r) => `${r.description ?? r.rule_id} ${r.rule_id}`, render: (r) => (
      <>
        {r.description ?? r.rule_id}
        <div className="muted mono">{r.rule_id}</div>
      </>
    ) },
    { key: 'count', header: t('Occorrenze'), type: 'number', align: 'left' },
  ]

  const colonneCve: Column<CveWazuh>[] = [
    { key: 'cve', header: 'CVE', mono: true, render: (v) => <>{cveAperte.has(v.cve) ? '▾ ' : '▸ '}{v.cve}</> },
    { key: 'severity', header: t('Gravità'), value: (v) => v.severity ?? '', render: (v) => (
      <span
        className="badge"
        style={{ background: coloreGravita(v.severity || ''), color: 'var(--accent-contrast)' }}
      >
        {v.severity ?? '—'}
      </span>
    ) },
    { key: 'score', header: t('Punteggio'), type: 'number', align: 'left', render: (v) => (typeof v.score === 'number' ? v.score.toFixed(1) : '—') },
    { key: 'agents', header: t('Host colpiti'), type: 'number', align: 'left' },
  ]

  const gravi =
    d?.vulnerabilities?.by_severity
      ?.filter((b) => ['critical', 'high'].includes((b.severity || '').toLowerCase()))
      .reduce((sum, b) => sum + (b.count ?? 0), 0) ?? 0

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">Wazuh</h1>
          <p className="page__subtitle">
            {t('Agenti, allarmi e vulnerabilità degli asset, sincronizzati in tempo reale con Wazuh.')}
          </p>
        </div>
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Interrogo…') : t('Aggiorna')}
        </button>
      </header>

      <ErrorBanner error={dati.error} />

      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : !d.available ? (
        <EmptyState title={t('Wazuh non è interrogabile.')} hint={d.reason ?? ''} />
      ) : (
        <>
          <div className="wazuh-kpi">
            <StatCard label={t('Agenti')} value={d.agents?.total ?? 0} />
            <StatCard label={t('attivo')} value={d.agents?.active ?? 0} tone="ok" />
            <StatCard
              label={t('Agenti con allarmi')}
              value={d.alerts?.agents_with_alerts ?? 0}
              tone={(d.alerts?.agents_with_alerts ?? 0) > 0 ? 'warn' : undefined}
            />
            <StatCard
              label={t('Allarmi ({giorni}g)', { giorni })}
              value={d.alerts?.total ?? 0}
              tone={(d.alerts?.total ?? 0) > 0 ? 'warn' : undefined}
            />
            <StatCard label={t('Vulnerabilità')} value={d.vulnerabilities?.total ?? 0} />
            <StatCard
              label={t('Critiche e alte')}
              value={gravi}
              tone={gravi > 0 ? 'error' : undefined}
            />
          </div>

          <Card title={t('Agenti per stato')}>
            <StatusStrip
              data={
                [
                  { key: 'active', label: t('attivo'), value: d.agents?.active ?? 0, tone: 'ok' },
                  { key: 'disconnected', label: t('disconnesso'), value: d.agents?.disconnected ?? 0, tone: 'warn' },
                  { key: 'pending', label: t('in attesa'), value: d.agents?.pending ?? 0, tone: 'info' },
                  { key: 'never', label: t('mai connesso'), value: d.agents?.never_connected ?? 0, tone: 'error' },
                ] as StatusSlice[]
              }
              emptyHint={t('Nessun agente registrato.')}
            />
          </Card>

          <Card
            title={t('Allarmi (ultimi {giorni} giorni)', { giorni })}
            actions={
              <div className="segmented">
                {FINESTRE.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={n === giorni ? 'segmented__on' : undefined}
                    onClick={() => setGiorni(n)}
                    disabled={dati.loading}
                  >
                    {t('{n}g', { n })}
                  </button>
                ))}
              </div>
            }
          >
            {!d.indexer_available ? (
              <p className="muted">
                {t('Allarmi e vulnerabilità richiedono l’Indexer di Wazuh, non configurato in questa credenziale.')}
              </p>
            ) : (d.alerts?.total ?? 0) === 0 ? (
              <p className="muted">{t('Nessun allarme di livello ≥ 7 nella finestra scelta.')}</p>
            ) : (
              <>
                {(d.alerts?.trend?.length ?? 0) > 1 && (
                  <>
                    <h4 className="wazuh-sub">{t('Andamento')}</h4>
                    <CountSeries
                      tone="warn"
                      label={t('Allarmi al giorno')}
                      points={(d.alerts?.trend ?? []).map((p) => ({
                        date: (p.date || '').slice(0, 10),
                        value: p.count ?? 0,
                      }))}
                    />
                  </>
                )}

                <h4 className="wazuh-sub">{t('Per livello')}</h4>
                <CategoryBars
                  data={(d.alerts?.by_level ?? []).map((b) => ({
                    key: String(b.level ?? '—'),
                    label: t('Livello {n}', { n: b.level ?? '—' }),
                    value: b.count ?? 0,
                  }))}
                />

                {(d.alerts?.by_rule?.length ?? 0) > 0 && (
                  <>
                    <h4 className="wazuh-sub">{t('Regole più frequenti')}</h4>
                    <DataTable
                      rows={d.alerts!.by_rule}
                      columns={colonneRegole}
                      rowKey={(r) => r.rule_id}
                      conteggio={{ nome: t('regole') }}
                    />
                  </>
                )}
              </>
            )}
          </Card>

          <Card title={t('Vulnerabilità')}>
            {!d.indexer_available ? (
              <p className="muted">
                {t('Allarmi e vulnerabilità richiedono l’Indexer di Wazuh, non configurato in questa credenziale.')}
              </p>
            ) : (d.vulnerabilities?.total ?? 0) === 0 ? (
              <p className="muted">{t('Nessuna vulnerabilità rilevata da Wazuh sul parco.')}</p>
            ) : (
              <>
                <div className="wazuh-due">
                  <div>
                    <h4 className="wazuh-sub">{t('Per gravità')}</h4>
                    <Donut
                      centreLabel={t('totali')}
                      slices={
                        (d.vulnerabilities?.by_severity ?? []).map((b) => ({
                          key: b.severity || '—',
                          label: b.severity || '—',
                          value: b.count ?? 0,
                          color: coloreGravita(b.severity || ''),
                        })) as DonutSlice[]
                      }
                    />
                  </div>
                  {(d.vulnerabilities?.top_agents?.length ?? 0) > 0 && (
                    <div>
                      <h4 className="wazuh-sub">{t('Host più colpiti')}</h4>
                      <CategoryBars
                        data={(d.vulnerabilities?.top_agents ?? []).map((a) => ({
                          key: a.name,
                          label: a.name || '—',
                          value: a.count ?? 0,
                        }))}
                      />
                    </div>
                  )}
                </div>

                {(d.vulnerabilities?.top_cves?.length ?? 0) > 0 && (
                  <>
                    <h4 className="wazuh-sub">{t('CVE più diffuse')}</h4>
                    <p className="field__hint" style={{ marginTop: 0 }}>
                      {t('Clicca una CVE per gli host colpiti e i pacchetti da aggiornare.')}
                    </p>
                    <DataTable
                      rows={d.vulnerabilities!.top_cves}
                      columns={colonneCve}
                      rowKey={(v) => v.cve}
                      onRowClick={(v) => alternaCve(v.cve)}
                      expanded={(v) => cveAperte.has(v.cve)}
                      expand={(v) => <HostCve cve={v.cve} />}
                      conteggio={{ nome: t('CVE') }}
                    />
                  </>
                )}
              </>
            )}
          </Card>

          {d.queried_at && (
            <p className="field__hint">
              {t('Interrogato: {quando}', { quando: formatDateTime(d.queried_at) })}
            </p>
          )}
        </>
      )}
      <h2 className="sezione-pagina__titolo" id="connessioni">{t('Connessioni')}</h2>
      <Connessioni funzioni={['wazuh']} segnaposto="wazuh" onChanged={() => dati.reload()} />
    </>
  )
}

/** Il dettaglio di una riga della tabella CVE: gli host colpiti e i pacchetti
 *  da aggiornare — la risposta a «host colpiti: 23, ma quali?». Gli host si
 *  chiedono solo alla prima apertura (query live via sonda). */
function HostCve({ cve }: { cve: string }) {
  const host = useAsync(() => api.wazuhVulnerabilityHosts(cve), [cve])
  const h = host.data

  const colonne: Column<WazuhCveHost>[] = [
    { key: 'agent', header: t('Host'), value: (riga) => `${riga.agent_name} ${riga.agent_id}`.trim(), render: (riga) => (
      <>
        {riga.agent_name || '—'}
        {riga.agent_id && <div className="muted mono">id {riga.agent_id}</div>}
      </>
    ) },
    { key: 'packages', header: t('Pacchetti da aggiornare'), value: (riga) => riga.packages
      .map((p) => `${p.name}${p.version ? ' ' + p.version : ''}`)
      .join(', ') },
  ]

  return host.loading && !h ? (
    <Spinner />
  ) : !h || !h.available ? (
    <p className="muted">{h?.reason ?? t('Wazuh non è interrogabile.')}</p>
  ) : (h.hosts?.length ?? 0) === 0 ? (
    <p className="muted">{t('Nessun host trovato per questa CVE.')}</p>
  ) : (
    <DataTable rows={h.hosts!} columns={colonne} rowKey={(riga) => riga.agent_id || riga.agent_name} conteggio={{ nome: t('host') }} />
  )
}
